import { useContext, useState } from "react";
import { useEffect } from "react";
import { Button } from "react-bootstrap";

import Spinner from "../../../../../shared/components/spinner/spinner";
import Tooltip from "../../../../../shared/components/tooltip/tooltip";
import AuthContext from "../../../../../shared/context/auth-context";
import useHttp from "../../../../../shared/hooks/use-http";
import { useHttpGet } from "../../../../../shared/hooks/use-httpNonTable";
import { postSingle } from "../../../../../shared/utils/api";
import { getData } from "../../../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../../../shared/utils/utils";
import { noop } from "../../../../utils";
import ControlledTable from "../../../controlled-table/controlled-table";
import {
  IColumn,
  ISortedBy,
  ITableRowData,
} from "../../../data-table/data-table";

export interface IRelatedAlertedEventWidget {
  id: string;
  type: "relatedAlertedEvent";
  modalType: "datamodelRelatedAlertedEvent";
  label: string;
  selectedColumns: IColumn[];
  datamodelId: string;
  modalData?: {
    // Extra information the modal needs to edit this widget
    [index: string]: string;
  };
  alertcaseRowId?: string;
  alertcaseId?: string;
}

interface IRelatedAlertedEventWidgetProps {
  widget: IRelatedAlertedEventWidget;
  previewMode?: boolean;
  alertcaseRowId?: string;
  alertcaseId?: string;
}

const RelatedAlertedEventWidget = ({
  widget,
  previewMode,
  alertcaseRowId,
  alertcaseId,
}: IRelatedAlertedEventWidgetProps) => {
  const limit = 50;
  const authCtx = useContext(AuthContext);
  const [data, setData] = useState<ITableRowData[]>([]);
  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({
    column: "",
    direction: "asc",
  });
  const [paginationOffset, setPaginationOffset] = useState(0);
  const [fullyLoaded, setFullyLoaded] = useState(false);
  const [isUserReadOnly, setIsUserReadOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [managerConfig, setManagerConfig] = useState<any>({}); // eslint-disable-line

  const {
    sendRequest: getTableData,
    status: getTableDataStatus,
    data: getTableDataResult,
    error: getTableDataError,
  } = useHttp(postSingle, false, authCtx.token, "datamodelAssociation", "manager");
  const {
    sendRequest: getManagerConfig,
    status: getManagerConfigStatus,
    data: getManagerConfigData
  } = useHttpGet(getData, false, authCtx.token, undefined, "manager");
  const {
    sendRequest: postLinkData,
    status: postLinkDataStatus,
    error: postLinkDataError
  } = useHttp(postSingle, false, authCtx.token, "linkTableUpdate", "manager");

  useEffect(() => {
    const roles = authCtx.selectedTenant.roles || [];
    setIsUserReadOnly(roles.length === 1 && roles[0] === "Read");
  }, [authCtx.selectedTenant]);

  useEffect(() => {
    if (previewMode) {
      const dummyData: ITableRowData[] = [];
      for (let i = 0; i < widget.selectedColumns.length; i++) {
        dummyData.push(
          widget.selectedColumns.reduce(
            (acc, curr) => {
              acc[curr.key] = "...";
              return acc;
            },
            { id: i.toString() } as ITableRowData
          )
        );
      }
      setData(dummyData);
      return;
    }
    if (alertcaseRowId && !fullyLoaded) {
      let querystring = `offset=${paginationOffset*limit}&limit=${limit}`;
      if (sortedByCol.column) {
        querystring += `&order_by=${sortedByCol.column} ${sortedByCol.direction}`;
      }

      getTableData(
        `datamodel_association?${querystring}`,
        {
          alertcase_id: alertcaseId,
          datamodel_id: widget.datamodelId,
          datamodel_type: "event",
          columns: widget.selectedColumns?.map((column) => column.key) || [],
          alerted_only: true
        },
        undefined,
        "none"
      );
    }
  }, [sortedByCol, alertcaseRowId, paginationOffset]);

  useEffect(() => {
    getManagerConfig("configuration");
  }, []);

  useEffect(() => {
    if (getManagerConfigStatus === "completed" && getManagerConfigData) {
      setManagerConfig(getManagerConfigData);
    }
  }, [getManagerConfigStatus]);

  useEffect(() => {
    if (
      getTableDataStatus === "completed" &&
      !getTableDataError &&
      getTableDataResult
    ) {
      setData([...data, ...getTableDataResult.rows]);
      if (getTableDataResult.rows.length < limit) {
        setFullyLoaded(true);
      }
    }
  }, [getTableDataStatus]);

  useEffect(() => {
    if (postLinkDataStatus === "completed" && !postLinkDataError) {
      setPaginationOffset(0);
      setFullyLoaded(false);
      setData([]);

      let querystring = `offset=${paginationOffset*limit}&limit=${limit}`;
      if (sortedByCol.column) {
        querystring += `&order_by=${sortedByCol.column} ${sortedByCol.direction}`;
      }

      getTableData(
        `datamodel_association?${querystring}`,
        {
          alertcase_id: alertcaseId,
          datamodel_id: widget.datamodelId,
          datamodel_type: "event",
          columns: widget.selectedColumns?.map((column) => column.key) || [],
          alerted_only: true
        },
        undefined,
        "none"
      );
    }
  }, [postLinkDataStatus, postLinkDataError, paginationOffset]);

  const handleMarkAsSuspiciousClick = () => {

    postLinkData(`update_link_table`, {
      event_id: widget.datamodelId,
      update_type: "related_alerted_events",
      sygno_ids: alertcaseRowId ? selectedIds.map((id) => id) : [],
      alertcase_row_id: alertcaseRowId,
      alertcase_id: alertcaseId,
      suspicious: true
    });
  };

  const handleMarkAsNotSuspiciousClick = () => {
    postLinkData(`update_link_table`, {
      event_id: widget.datamodelId,
      update_type: "related_alerted_events",
      sygno_ids: alertcaseRowId ? selectedIds.map((id) => id) : [],
      alertcase_row_id: alertcaseRowId,
      alertcase_id: alertcaseId,
      suspicious: false
    });
  };

  return (
    <div className="relatedAlertedEventWidget">
      {validateLoadingStatusNew([getTableDataStatus, getManagerConfigStatus, postLinkDataStatus]) && <Spinner />}
      <ControlledTable
        readOnly={isUserReadOnly}
        title={`Related alerted events (${widget.label})`}
        allowSelectAll={false}
        data={data}
        style="compact"
        selectionMode="multi"
        columns={[
          ...widget.selectedColumns,
          {
            key: "sygno_suspicious",
            displayName: managerConfig.suspicious_definition || "Suspicious",
            dataType: { type: "text" },
          },
        ]}
        onSorted={(sortedByCol: ISortedBy) => {
          setSortedByCol(sortedByCol);
          setData([]);
          setPaginationOffset(0);
          setFullyLoaded(false);
        }}
        onScroll={() => setPaginationOffset((prev) => prev + 1)}
        onRowClick={noop}
        onSelectionChange={setSelectedIds}
        onSaveClicked={handleMarkAsSuspiciousClick}
        customSaveButtonTooltip={`Mark as: ${
          managerConfig.suspicious_positive || "suspicious"
        }`}
        customSaveButtonText={`Mark as: ${
          managerConfig.suspicious_positive || "suspicious"
        }`}
        disableSaveButton={previewMode || isUserReadOnly || selectedIds.length === 0}
        extraButtons={
          <Tooltip
            content={
              "Mark as: " +
              (managerConfig.suspicious_negative || "not suspicious")
            }
            direction="top"
            delay={350}
          >
            <Button
              onClick={handleMarkAsNotSuspiciousClick}
              className="popup-action"
              disabled={previewMode || isUserReadOnly || selectedIds.length === 0}
            >
              Mark as:{" "}
              {managerConfig.suspicious_negative || "not suspicious"}
            </Button>
          </Tooltip>
        }
        showFalse={true}
      />
    </div>
  );
};

export default RelatedAlertedEventWidget;
